import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaGraduationCap, FaUpload, FaFileAlt, FaFileWord, FaFilePdf, FaCloudUploadAlt, FaRegFileAlt, FaTrash, FaEye } from 'react-icons/fa';
import { uploadFiles, trainBot, getTrainingData, deleteTrainingData } from '../api';
import MobileLayout, { MOBILE_BREAKPOINT } from './MobileLayout';

// Constants for file validation
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Training tips data
const TRAINING_TIPS = [
  'Keep content clear and concise',
  'Include relevant examples',
  'Use proper formatting',
  'Review content before upload'
];

const Training = () => {
  const navigate = useNavigate();
  // State management for form inputs and UI state
  const [activeTab, setActiveTab] = useState('text');
  const [trainingText, setTrainingText] = useState('');
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Check training data on mount
  useEffect(() => {
    const checkTrainingData = async () => {
      try {
        const result = await getTrainingData();
        if (result.status === 'success') {
          setTrainingText(result.data);
        }
      } catch (error) {
        console.error('Error fetching initial training data:', error);
      }
    };
    
    if (!isMobileView) {
      checkTrainingData();
    }
  }, [isMobileView]);

  if (isMobileView) {
    return (
      <MobileLayout 
        pageTitle="Training"
        restrictionTitle="Training Requires More Space"
        restrictionMessage="Training your AI chatbot requires careful data input, document management, and precise configurations. These operations are optimized for larger screens to ensure accurate training results."
        features={[
          "Upload and manage training documents easily",
          "Edit and format training text with precision",
          "Preview and validate training content",
          "Monitor training progress in detail"
        ]}
        proTip="For the best training experience, please use an iPad, tablet, laptop, or desktop computer to train your AI chatbot."
      />
    );
  }

  // Resets form state to initial values
  const resetForm = () => {
    setTrainingText('');
    setFiles([]);
    setActiveTab('text');
  };

  // Handles file selection from input
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    validateAndSetFiles(selectedFiles);
  };

  // Validates files based on extension and size, then updates state
  const validateAndSetFiles = (selectedFiles) => {
    const validFiles = selectedFiles.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return ALLOWED_EXTENSIONS.includes(extension) && file.size <= MAX_FILE_SIZE;
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error(`Some files were rejected. Please only upload ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()} files under ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
    }

    setFiles(validFiles);
  };

  // Handles drag and drop events for file upload
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Handles drag leave event for file upload
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Handles drop event for file upload
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndSetFiles(droppedFiles);
  };

  // Prepares text data for training based on active tab
  const prepareTrainingDocuments = async () => {
    if (activeTab === 'text') {
      if (!trainingText.trim()) {
        throw new Error('Please enter some text for training');
      }
      return trainingText.trim();
    }
    
    if (files.length === 0) {
      throw new Error('Please select files for training');
    }

    const uploadedTexts = await uploadFiles(files);
    return uploadedTexts.map(doc => doc.text).join('\n\n');
  };

  // Prepares the training request
  const prepareTrainingRequest = async (documents) => {
    try {
      return await trainBot(documents);
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please ensure you are logged in to train the chatbot');
      }
      throw error;
    }
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const documents = await prepareTrainingDocuments();
      const result = await prepareTrainingRequest(documents);
      
      toast.success(result.message || 'Training completed successfully!');
      resetForm();
    } catch (err) {
      console.error('Training error:', err);
      toast.error(err.message || 'Failed to train bot');
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to handle delete
  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await deleteTrainingData();
      toast.success('Training data deleted successfully');
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error(error.error || 'Failed to delete training data');
    } finally {
      setIsLoading(false);
    }
  };

  // Add fetchTrainingData function
  const fetchTrainingData = async () => {
    setIsLoadingData(true);
    try {
      const result = await getTrainingData();
      if (result.status === 'success') {
        setTrainingText(result.data);
        setActiveTab('text');
      } else {
        toast.error(result.error || 'Failed to fetch training data');
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error(error.error || 'Failed to fetch training data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Add confirmation popup component
  const DeleteConfirmationPopup = () => (
    showDeleteConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
          <p className="text-gray-500 mb-6">Are you sure you want to delete the training data? This action cannot be undone.</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    )
  );

  // Renders the header section
  const renderHeader = () => (
    <div className="mb-6 flex justify-between items-center">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Train Your Chatbot</h1>
        <p className="text-gray-600">Enhance your chatbot's knowledge with training data</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={fetchTrainingData}
          disabled={isLoadingData}
          className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors flex items-center text-sm ${
            isLoadingData ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
          }`}
        >
          <FaEye className="w-4 h-4 mr-2" />
          {isLoadingData ? 'Loading...' : 'View Training Data'}
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isLoading}
          className="px-4 py-2 bg-red-600 text-white rounded-lg transition-colors flex items-center text-sm hover:bg-red-700 disabled:opacity-50"
        >
          <FaTrash className="w-4 h-4 mr-2" />
          Delete Training Data
        </button>
      </div>
    </div>
  );

  // Renders tab buttons for switching between input modes
  const renderTabs = () => (
    <div className="flex border-b">
      {[
        { id: 'text', icon: FaRegFileAlt, label: 'Text Input' },
        { id: 'file', icon: FaCloudUploadAlt, label: 'Upload Document' }
      ].map(tab => (
        <button
          key={tab.id}
          className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
          onClick={() => setActiveTab(tab.id)}
        >
          <tab.icon className="w-4 h-4 mr-2" />
          {tab.label}
        </button>
      ))}
    </div>
  );

  // Renders text input section
  const renderTextInput = () => (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-800">Training Content</h2>
          <p className="text-sm text-gray-500 mt-1">Enter the text you want your chatbot to learn from</p>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors flex items-center text-sm ${
            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
          }`}
        >
          <FaUpload className="w-4 h-4 mr-2" />
          {isLoading ? 'Training...' : 'Start Training'}
        </button>
      </div>
      <textarea
        value={trainingText}
        onChange={(e) => setTrainingText(e.target.value)}
        disabled={isLoading}
        className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[400px] text-gray-700 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        placeholder="Enter your training content here..."
      />
    </div>
  );

  // Renders file upload section
  const renderFileUploadHeader = () => (
    <div className="flex justify-between items-center mb-4">
      <div>
        <h2 className="text-lg font-medium text-gray-800">Document Upload</h2>
        <p className="text-sm text-gray-500 mt-1">Upload PDF, DOCX, or TXT files for training</p>
      </div>
    </div>
  );

  // Renders the file upload section
  const renderFileUpload = () => (
    <div>
      {renderFileUploadHeader()}
      <div
        className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <FaCloudUploadAlt className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {isLoading ? 'Uploading...' : 'Drag and drop your files here'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">or</p>
          {renderFileUploadInput()}
          {files.length > 0 && renderFileList()}
        </div>
      </div>
      {files.length > 0 && renderSubmitButton()}
    </div>
  );

  const renderFileUploadInput = () => (
    <label className="relative cursor-pointer">
      <input
        type="file"
        className="hidden"
        accept=".pdf,.docx,.doc,.txt"
        onChange={handleFileChange}
        multiple
        disabled={isLoading}
      />
      <span className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors inline-flex items-center ${
        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
      }`}>
        <FaUpload className="w-4 h-4 mr-2" />
        Choose Files
      </span>
    </label>
  );

  const renderFileList = () => (
    <div className="mt-6 space-y-2">
      {files.map((file, index) => (
        <div key={index} className="p-3 bg-blue-50 rounded-lg inline-flex items-center">
          {file.name.endsWith('.pdf') ? (
            <FaFilePdf className="w-5 h-5 text-red-500 mr-2" />
          ) : file.name.endsWith('.txt') ? (
            <FaFileAlt className="w-5 h-5 text-gray-500 mr-2" />
          ) : (
            <FaFileWord className="w-5 h-5 text-blue-600 mr-2" />
          )}
          <span className="text-sm text-gray-700">{file.name}</span>
        </div>
      ))}
    </div>
  );

  // Renders submit button
  const renderSubmitButton = () => (
    <div className="flex justify-end mt-4">
      <button
        type="submit"
        disabled={isLoading}
        className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors flex items-center text-sm ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
        }`}
      >
        <FaUpload className="w-4 h-4 mr-2" />
        {isLoading ? 'Training...' : 'Start Training'}
      </button>
    </div>
  );

  // Renders training tips sidebar
  const renderTips = () => (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-5">
      <div className="p-3 bg-blue-50 rounded-lg inline-block mb-4">
        <FaGraduationCap className="w-6 h-6 text-blue-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-800 mb-2">Training Tips</h3>
      <div className="space-y-3">
        {TRAINING_TIPS.map((tip, index) => (
          <p key={index} className="text-sm text-gray-600">• {tip}</p>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs text-gray-500">
          Supported formats: {ALLOWED_EXTENSIONS.join(', ').toUpperCase()}
          <br />
          Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)}MB
        </p>
      </div>
    </div>
  );

  // Renders enterprise section
  const renderEnterpriseSection = () => (
    <div className="mt-8 bg-blue-50/50 border border-blue-100 rounded-lg p-5">
      <h3 className="text-lg font-medium text-blue-800 mb-2">Need More?</h3>
      <p className="text-sm text-blue-700 mb-3">
        For advanced features (calendar booking, product showcases, human transfer, Lead generation, etc.), 
        our team can build custom solutions.
      </p>
      <button 
        onClick={() => window.open('https://www.curosai.com', '_blank')}
        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        Contact Enterprise Team →
      </button>
    </div>
  );

  // Renders main content area
  const renderMainContent = () => (
    <div className="lg:col-span-3">
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
        {renderTabs()}
        <div className="p-6">
              <form onSubmit={handleSubmit}>
            {activeTab === 'text' ? renderTextInput() : renderFileUpload()}
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {renderHeader()}
      <DeleteConfirmationPopup />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mb-6">
        {renderMainContent()}
        <div>
          {renderTips()}
          {renderEnterpriseSection()}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Training;
